import { Component, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { FollowProvider } from "@/context/FollowContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AuthProvider } from "@/hooks/useAuth";
import { BackgroundScansProvider } from "@/hooks/useBackgroundScans";
import { ThemeProvider } from "@/context/ThemeContext";
import { OfflineGate } from "@/components/OfflineGate";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { GrailMatchListener } from "@/components/GrailMatchListener";
import { NewBadgeToaster } from "@/components/NewBadgeToaster";
import { TourOverlay } from "@/components/TourOverlay";
import { ScrollToTop } from "@/components/ScrollToTop";

import Index from "./pages/Index.tsx";
import NotFound from "./pages/NotFound.tsx";
import Auth from "./pages/Auth.tsx";
import Signup from "./pages/Signup.tsx";
import ResetPassword from "./pages/ResetPassword.tsx";
import Home from "./pages/Home.tsx";
import Scan from "./pages/Scan.tsx";
import ScanQuick from "./pages/ScanQuick.tsx";
import BulkVerify from "./pages/BulkVerify.tsx";
import BulkReview from "./pages/BulkReview.tsx";
import Review from "./pages/Review.tsx";
import Analyze from "./pages/Analyze.tsx";
import VerifyExisting from "./pages/VerifyExisting.tsx";
import Result from "./pages/Result.tsx";
import Price from "./pages/Price.tsx";
import ListForSale from "./pages/ListForSale.tsx";
import Share from "./pages/Share.tsx";
import History from "./pages/History.tsx";
import Posted from "./pages/Posted.tsx";
import Certificate from "./pages/Certificate.tsx";
import Archive from "./pages/Archive.tsx";
import Comps from "./pages/Comps.tsx";
import Feed from "./pages/Feed.tsx";
import Profile from "./pages/Profile.tsx";
import Connections from "./pages/Connections.tsx";
import ListingStatusEdit from "./pages/ListingStatusEdit.tsx";
import Badges from "./pages/Badges.tsx";
import FindOfTheWeek from "./pages/FindOfTheWeek.tsx";
import Grails from "./pages/Grails.tsx";
import { Navigate } from "react-router-dom";
import Messages from "./pages/Messages.tsx";
import MessageThread from "./pages/MessageThread.tsx";
import Notifications from "./pages/Notifications.tsx";
import Leaders from "./pages/Leaders.tsx";
import Brand from "./pages/Brand.tsx";
import Brands from "./pages/Brands.tsx";
import Tutorial from "./pages/Tutorial.tsx";
import BestPractices from "./pages/BestPractices.tsx";
import FAQ from "./pages/FAQ.tsx";
import VerdictLegend from "./pages/VerdictLegend.tsx";
import WhatsNew from "./pages/WhatsNew.tsx";
import Settings from "./pages/Settings.tsx";
import SettingsAccessibility from "./pages/SettingsAccessibility.tsx";
import SettingsAccessibilityHub from "./pages/SettingsAccessibilityHub.tsx";
import SettingsNotifications from "./pages/SettingsNotifications.tsx";
import Privacy from "./pages/Privacy.tsx";
import Support from "./pages/Support.tsx";
import Terms from "./pages/Terms.tsx";
import CommunityGuidelines from "./pages/CommunityGuidelines.tsx";
import Wants from "./pages/Wants.tsx";
import PostChoice from "./pages/PostChoice.tsx";
import Post from "./pages/Post.tsx";
import QuickPost from "./pages/QuickPost.tsx";
import Admin from "./pages/Admin.tsx";
import AdminPricingTelemetry from "./pages/AdminPricingTelemetry.tsx";
import Onboard from "./pages/Onboard.tsx";
import AuthCallback from "./pages/AuthCallback.tsx";
import ModeratorApplication from "./pages/ModeratorApplication.tsx";
import ModeratorInvite from "./pages/ModeratorInvite.tsx";

class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, fontFamily: "'IBM Plex Mono', monospace", fontSize: 13 }}>
          <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 24, marginBottom: 12 }}>Something went wrong</div>
          <div style={{ opacity: 0.6, marginBottom: 24 }}>{(this.state.error as Error).message}</div>
          <button onClick={() => window.location.assign("/")} style={{ padding: "10px 20px", cursor: "pointer" }}>
            Go home
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const queryClient = new QueryClient();

const protect = (el: JSX.Element) => <ProtectedRoute>{el}</ProtectedRoute>;

const App = () => (
  <AppErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <OfflineGate>
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <FollowProvider>
              <BackgroundScansProvider>
              <GrailMatchListener />
              <NewBadgeToaster />
              <TourOverlay />
              <ScrollToTop />
              <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/onboard" element={<Onboard />} />
              <Route path="/tutorial" element={protect(<Tutorial />)} />
              <Route path="/best-practices" element={<BestPractices />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/verdict-legend" element={<VerdictLegend />} />
              <Route path="/whats-new" element={<WhatsNew />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/support" element={<Support />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/community-guidelines" element={<CommunityGuidelines />} />
              <Route path="/home" element={protect(<Home />)} />
              <Route path="/post" element={protect(<PostChoice />)} />
              <Route path="/post/quick" element={protect(<QuickPost />)} />
              <Route path="/scan" element={protect(<Scan />)} />
              <Route path="/scan/quick" element={protect(<ScanQuick />)} />
              <Route path="/bulk" element={protect(<BulkVerify />)} />
              <Route path="/bulk/review" element={protect(<BulkReview />)} />
              <Route path="/review" element={protect(<Review />)} />
              <Route path="/analyze" element={protect(<Analyze />)} />
              <Route path="/verify-existing" element={protect(<VerifyExisting />)} />
              <Route path="/result" element={protect(<ErrorBoundary><Result /></ErrorBoundary>)} />
              <Route path="/price" element={protect(<Price />)} />
              <Route path="/list" element={protect(<ListForSale />)} />
              <Route path="/share" element={protect(<Share />)} />
              <Route path="/history" element={protect(<History />)} />
              <Route path="/posted" element={protect(<Posted />)} />
              <Route path="/listing/:scanId/edit" element={protect(<ListingStatusEdit />)} />
              <Route path="/cert" element={protect(<Certificate />)} />
              <Route path="/archive" element={<Archive />} />
              <Route path="/comps" element={<Comps />} />
              <Route path="/feed" element={<Feed />} />
              <Route path="/post/:id" element={<Post />} />
              <Route path="/you" element={protect(<Profile forceYou />)} />
              <Route path="/you/followers" element={protect(<Connections forceYou kind="followers" />)} />
              <Route path="/you/following" element={protect(<Connections forceYou kind="following" />)} />
              <Route path="/you/badges" element={protect(<Badges />)} />
              <Route path="/find-of-the-week" element={protect(<FindOfTheWeek />)} />
              <Route path="/profile/:handle" element={<Profile />} />
              <Route path="/profile/:handle/followers" element={<Connections kind="followers" />} />
              <Route path="/profile/:handle/following" element={<Connections kind="following" />} />
              <Route path="/settings" element={protect(<Settings />)} />
              <Route path="/settings/accessibility-hub" element={protect(<SettingsAccessibilityHub />)} />
              <Route path="/settings/accessibility" element={protect(<SettingsAccessibility />)} />
              <Route path="/settings/notifications" element={protect(<SettingsNotifications />)} />
              <Route path="/settings/moderator-application" element={protect(<ModeratorApplication />)} />
              <Route path="/moderator-invite/:id" element={protect(<ModeratorInvite />)} />
              <Route path="/admin" element={protect(<Admin />)} />
              <Route path="/admin/pricing-telemetry" element={protect(<AdminPricingTelemetry />)} />
              <Route path="/settings/feedback-review" element={<Navigate to="/admin" replace />} />
              <Route path="/grails" element={protect(<Grails />)} />
              <Route path="/wants" element={protect(<Wants />)} />
              <Route path="/messages" element={protect(<Messages />)} />
              <Route path="/messages/:id" element={protect(<MessageThread />)} />
              <Route path="/notifications" element={protect(<Notifications />)} />
              <Route path="/leaders" element={<Leaders />} />
              <Route path="/brand/:name" element={<Brand />} />
              <Route path="/brands" element={<Brands />} />
              <Route path="*" element={<NotFound />} />
              </Routes>
              </BackgroundScansProvider>
            </FollowProvider>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
      </OfflineGate>
    </TooltipProvider>
  </QueryClientProvider>
  </AppErrorBoundary>
);

export default App;
